import {CheckCircle, ChevronDown, ChevronRight, Info, MinusCircle, XCircle} from 'lucide-react';
import {useState} from 'react';
import {Switch} from '@common-web/components/ui/switch';
import {Label} from '@common-web/components/ui/label';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import {
  MENU_FLAGS,
  type ClientFeaturesShape,
  type MenuAudience,
  type MenuFlagDefinition,
  type MenuKey,
  isMenuFlagVisibleForClient,
  resolveAppUserDeactivationEnabled,
  resolveBrokerCommissionAmountEnabled,
  resolveBrokerDocumentSelfServiceEnabled,
  resolveLinkVbaGroupEnabled,
  resolveMenuItemEnabled,
  resolveNewBrokerUiEnabled,
  resolveOpenEnrollmentDashboardEnabled,
  resolveResourceFoldersEnabled,
  resolveSimplifiedResourceLayoutEnabled,
  resolveRfcClientPortalAccessEnabled,
  resolveExternalTpasEnabled,
  resolveVipEnabled,
  resolveRfcAchAuthorizationEnabled,
  resolveViewAsBrokerEnabled,
} from '@common/menuFlags';

const AUDIENCE_SECTIONS: Array<{audience: MenuAudience; title: string; description: string}> = [
  {
    audience: 'broker',
    title: 'Broker Portal',
    description:
      'Items shown to broker users. The "Enhanced Broker Portal" master toggle controls the default for every broker item; per-item overrides win when set.',
  },
  {
    audience: 'client',
    title: 'Client Admin Portal',
    description:
      'Items shown to client/supergroup admins. All items default to visible; turn an override off to hide one for this tenant.',
  },
  {
    audience: 'group',
    title: 'Group Portal',
    description:
      'Items shown to group users (e.g., HR admins for a single employer). Defaults visible.',
  },
];

type OverrideState = 'default' | 'on' | 'off';

function overrideStateFor(
  features: ClientFeaturesShape | null | undefined,
  key: MenuKey,
): OverrideState {
  const override = features?.menuOverrides?.[key];
  if (override === true) return 'on';
  if (override === false) return 'off';
  return 'default';
}

function defaultLabel(flag: MenuFlagDefinition, master: boolean): string {
  if (flag.defaultVisible === 'master') {
    return master ? 'Default: visible (master on)' : 'Default: hidden (master off)';
  }
  if (typeof flag.defaultVisible === 'object') {
    return `Default: follows "${flag.defaultVisible.feature}" feature`;
  }
  return flag.defaultVisible ? 'Default: visible' : 'Default: hidden';
}

export function ClientFeatureFlagDisplay({
  features,
  clientName,
}: {
  features: ClientFeaturesShape | null | undefined;
  clientName?: string | null;
}) {
  const overrides = features?.menuOverrides ?? {};
  const overrideCount = Object.values(overrides).filter((v) => typeof v === 'boolean').length;

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Enhanced Broker Portal</div>
        {features?.enhancedBrokerPortal ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Per-menu overrides</div>
        <Badge variant='outline'>{overrideCount} set</Badge>
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Resource folders</div>
        {resolveResourceFoldersEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>RFC client portal access step</div>
        {resolveRfcClientPortalAccessEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>RFC ACH authorization step</div>
        {resolveRfcAchAuthorizationEnabled(features, clientName) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>
          Simplified resource card &amp; folder layout
        </div>
        {resolveSimplifiedResourceLayoutEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Link existing VBA group</div>
        {resolveLinkVbaGroupEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Broker document self-service</div>
        {resolveBrokerDocumentSelfServiceEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>VIP members</div>
        {resolveVipEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>App user deactivation</div>
        {resolveAppUserDeactivationEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Bulk broker import</div>
        {features?.bulkBrokerImportEnabled ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>View as broker</div>
        {resolveViewAsBrokerEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>New Broker Portal UI</div>
        {resolveNewBrokerUiEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Open Enrollment dashboard</div>
        {resolveOpenEnrollmentDashboardEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-sm text-brand-navy font-medium'>Broker commission amount</div>
        {resolveBrokerCommissionAmountEnabled(features) ? (
          <CheckCircle className='h-4 w-4 text-emerald shrink-0' />
        ) : (
          <XCircle className='h-4 w-4 text-gray-300 shrink-0' />
        )}
      </div>
    </div>
  );
}

type FeatureFlagControlsProps = {
  features: ClientFeaturesShape;
  /** Tenant name, used to gate client-restricted menu flag toggles (e.g. BEEP). */
  clientName: string;
  disabled?: boolean;
  onMasterChange: (checked: boolean) => void;
  onOverrideChange: (key: MenuKey, next: OverrideState) => void;
  onResourceFoldersChange: (checked: boolean) => void;
  onTicketingChange: (checked: boolean) => void;
  onRfcClientPortalAccessChange: (checked: boolean) => void;
  onRfcAchAuthorizationChange: (checked: boolean) => void;
  onSimplifiedResourceLayoutChange: (checked: boolean) => void;
  onLinkVbaGroupChange: (checked: boolean) => void;
  onBrokerDocumentSelfServiceChange: (checked: boolean) => void;
  onVipChange: (checked: boolean) => void;
  onExternalTpasChange: (checked: boolean) => void;
  onAppUserDeactivationChange: (checked: boolean) => void;
  onBrokerCommissionAmountChange: (checked: boolean) => void;
  onBulkBrokerImportChange: (checked: boolean) => void;
  onViewAsBrokerChange: (checked: boolean) => void;
  onNewBrokerUiChange: (checked: boolean) => void;
  onOpenEnrollmentDashboardChange: (checked: boolean) => void;
  idPrefix: string;
};

export function ClientFeatureFlagControls({
  features,
  clientName,
  disabled,
  onMasterChange,
  onOverrideChange,
  onResourceFoldersChange,
  onTicketingChange,
  onRfcClientPortalAccessChange,
  onRfcAchAuthorizationChange,
  onSimplifiedResourceLayoutChange,
  onLinkVbaGroupChange,
  onBrokerDocumentSelfServiceChange,
  onVipChange,
  onExternalTpasChange,
  onAppUserDeactivationChange,
  onBrokerCommissionAmountChange,
  onBulkBrokerImportChange,
  onViewAsBrokerChange,
  onNewBrokerUiChange,
  onOpenEnrollmentDashboardChange,
  idPrefix,
}: FeatureFlagControlsProps) {
  const resourceFoldersId = `${idPrefix}-resource-folders`;
  const ticketingId = `${idPrefix}-ticketing`;
  const rfcClientPortalAccessId = `${idPrefix}-rfc-client-portal-access`;
  const rfcAchAuthorizationId = `${idPrefix}-rfc-ach-authorization`;
  const simplifiedResourceLayoutId = `${idPrefix}-simplified-resource-layout`;
  const linkVbaGroupId = `${idPrefix}-link-vba-group`;
  const brokerDocumentSelfServiceId = `${idPrefix}-broker-document-self-service`;
  const vipId = `${idPrefix}-vip`;
  const externalTpasId = `${idPrefix}-external-tpas`;
  const appUserDeactivationId = `${idPrefix}-app-user-deactivation`;
  const bulkBrokerImportId = `${idPrefix}-bulk-broker-import`;
  const viewAsBrokerId = `${idPrefix}-view-as-broker`;
  const newBrokerUiId = `${idPrefix}-new-broker-ui`;
  const openEnrollmentDashboardId = `${idPrefix}-open-enrollment-dashboard`;
  const brokerCommissionAmountId = `${idPrefix}-broker-commission-amount`;
  return (
    <div className='space-y-6'>
      <div className='border border-gray-200 rounded-lg bg-white p-4 space-y-4'>
        <div className='text-sm font-semibold text-brand-navy'>Tenant Features</div>
        <div className='flex items-start gap-3'>
          <Switch
            id={resourceFoldersId}
            checked={resolveResourceFoldersEnabled(features)}
            disabled={disabled}
            onCheckedChange={onResourceFoldersChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={resourceFoldersId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Tag-based Resource Folders
            </Label>
            <p className='text-sm text-brand-gray'>
              Organize the broker and client-admin resource libraries into tag-driven folders. Turn
              off to show a flat list of resources instead. Defaults to on.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={simplifiedResourceLayoutId}
            checked={resolveSimplifiedResourceLayoutEnabled(features)}
            disabled={disabled}
            onCheckedChange={onSimplifiedResourceLayoutChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={simplifiedResourceLayoutId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Simplified Resource Card &amp; Folder Layout
            </Label>
            <p className='text-sm text-brand-gray'>
              Switches the broker and client-admin resource screens to the simplified folder-card
              layout (drill-in file cards, tag-named folder titles). Turn off to keep the standard
              resource screens. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={ticketingId}
            checked={!!features.ticketing}
            disabled={disabled}
            onCheckedChange={onTicketingChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label htmlFor={ticketingId} className='font-semibold cursor-pointer text-brand-navy'>
              Portal Ticketing System
            </Label>
            <p className='text-sm text-brand-gray'>
              Adds the broker↔admin ticket flow under <code>/tickets</code> for client admins and{' '}
              <code>/broker/tickets</code> for brokers. Turning on for the first time prompts for
              the ticket-ID prefix (e.g. <code>SYN-</code>) and the branded email sender. Defaults
              to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={rfcClientPortalAccessId}
            checked={resolveRfcClientPortalAccessEnabled(features)}
            disabled={disabled}
            onCheckedChange={onRfcClientPortalAccessChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={rfcClientPortalAccessId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              RFC Client Portal Access Step
            </Label>
            <p className='text-sm text-brand-gray'>
              Shows the "Client Portal Access" step in the RFC wizard, where submitters add users
              who should get client-portal logins. Turn off to drop the step (and its review
              section) for this tenant. Defaults to on.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={rfcAchAuthorizationId}
            checked={resolveRfcAchAuthorizationEnabled(features, clientName)}
            disabled={disabled}
            onCheckedChange={onRfcAchAuthorizationChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={rfcAchAuthorizationId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              RFC ACH Authorization Step
            </Label>
            <p className='text-sm text-brand-gray'>
              Shows the "ACH Authorization" step in the RFC wizard (plus the bank-document upload
              and every ACH/banking mention in the password-gated flow). Turn off to drop it for
              this tenant. Defaults to on for every tenant except Synrgy, which defaults off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={linkVbaGroupId}
            checked={resolveLinkVbaGroupEnabled(features)}
            disabled={disabled}
            onCheckedChange={onLinkVbaGroupChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={linkVbaGroupId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Link Existing VBA Group
            </Label>
            <p className='text-sm text-brand-gray'>
              Adds the client-admin "Link existing VBA group" action on the Account → Group Info
              tab, used to adopt a group CBS pre-created in VBA. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={brokerDocumentSelfServiceId}
            checked={resolveBrokerDocumentSelfServiceEnabled(features)}
            disabled={disabled}
            onCheckedChange={onBrokerDocumentSelfServiceChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={brokerDocumentSelfServiceId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Broker Document Self-Service
            </Label>
            <p className='text-sm text-brand-gray'>
              Lets brokers upload, replace, and delete their own personal documents (W-2, I-9,
              licenses, etc.) on the broker Documents page. Turn off to make the page read-only
              (view/download only), leaving uploads to client admins acting on the broker's behalf.
              Defaults to on.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={vipId}
            checked={resolveVipEnabled(features)}
            disabled={disabled}
            onCheckedChange={onVipChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label htmlFor={vipId} className='font-semibold cursor-pointer text-brand-navy'>
              VIP Members
            </Label>
            <p className='text-sm text-brand-gray'>
              Enables VIP (CleverBenefits-only) member management for this tenant: the "Add VIP
              Member" button on member search, VIP rows in the results, and the add-VIP page.
              Requires a VIP adapter for the tenant. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={externalTpasId}
            checked={resolveExternalTpasEnabled(features)}
            disabled={disabled}
            onCheckedChange={onExternalTpasChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={externalTpasId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              External TPAs
            </Label>
            <p className='text-sm text-brand-gray'>
              Surfaces members and groups administered by organizations other than CBS, each sending
              its own eligibility file. Adds them to member search, group search and the CSV export,
              along with the read-only detail pages and the "Administered by" column and filter.
              Requires an external-TPA adapter for the tenant, and the nightly group sync only
              covers tenants with this on. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={appUserDeactivationId}
            checked={resolveAppUserDeactivationEnabled(features)}
            disabled={disabled}
            onCheckedChange={onAppUserDeactivationChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={appUserDeactivationId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              App User Deactivation
            </Label>
            <p className='text-sm text-brand-gray'>
              Adds the "Deactivate user" action to the App User card on member details, for admins
              of the member&apos;s group. Deactivating turns off the member&apos;s CleverBenefits
              app account; enrollment is unaffected. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={bulkBrokerImportId}
            checked={!!features.bulkBrokerImportEnabled}
            disabled={disabled}
            onCheckedChange={onBulkBrokerImportChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={bulkBrokerImportId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Bulk Broker Import
            </Label>
            <p className='text-sm text-brand-gray'>
              Adds the broker CSV import wizard under <code>/brokers/import</code> for client
              admins, plus an "Import Brokers" button on the broker tree. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={viewAsBrokerId}
            checked={resolveViewAsBrokerEnabled(features)}
            disabled={disabled}
            onCheckedChange={onViewAsBrokerChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={viewAsBrokerId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              View as Broker
            </Label>
            <p className='text-sm text-brand-gray'>
              Lets client admins preview the broker portal "as" one of their active brokers
              (read-only impersonation) from the broker detail page. Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={newBrokerUiId}
            checked={resolveNewBrokerUiEnabled(features)}
            disabled={disabled}
            onCheckedChange={onNewBrokerUiChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label htmlFor={newBrokerUiId} className='font-semibold cursor-pointer text-brand-navy'>
              New Broker Portal UI
            </Label>
            <p className='text-sm text-brand-gray'>
              Switches the entire portal to the redesigned broker UI: cream surface, dark sidebar,
              and restyled broker + broker-management pages. Turn off to keep the legacy look.
              Defaults to off.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={openEnrollmentDashboardId}
            checked={resolveOpenEnrollmentDashboardEnabled(features)}
            disabled={disabled}
            onCheckedChange={onOpenEnrollmentDashboardChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={openEnrollmentDashboardId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Open Enrollment Dashboard
            </Label>
            <p className='text-sm text-brand-gray'>
              Adds the Open Enrollment dashboard for group HR admins under{' '}
              <code>/groups/:groupId/open-enrollment</code> — registration progress, the
              not-yet-registered roster, and a CSV export. Also controls the group-portal sidebar
              entry, the API, and the default landing page for single-group users (dashboard when
              on, group detail when off). Defaults to on.
            </p>
          </div>
        </div>
        <div className='flex items-start gap-3 pt-3 border-t border-gray-100'>
          <Switch
            id={brokerCommissionAmountId}
            checked={resolveBrokerCommissionAmountEnabled(features)}
            disabled={disabled}
            onCheckedChange={onBrokerCommissionAmountChange}
            className='mt-1'
          />
          <div className='space-y-1'>
            <Label
              htmlFor={brokerCommissionAmountId}
              className='font-semibold cursor-pointer text-brand-navy'
            >
              Broker Commission Amount
            </Label>
            <p className='text-sm text-brand-gray'>
              Shows the Commission Amount box on the broker detail page, where client admins can
              edit it, and as a read-only card on the broker&apos;s own profile. Defaults to off.
            </p>
          </div>
        </div>
      </div>

      {AUDIENCE_SECTIONS.map((section) => (
        <AudienceSection
          key={section.audience}
          audience={section.audience}
          title={section.title}
          description={section.description}
          features={features}
          clientName={clientName}
          disabled={disabled}
          onMasterChange={onMasterChange}
          onOverrideChange={onOverrideChange}
          idPrefix={idPrefix}
        />
      ))}
    </div>
  );
}

function AudienceSection({
  audience,
  title,
  description,
  features,
  clientName,
  disabled,
  onMasterChange,
  onOverrideChange,
  idPrefix,
}: {
  audience: MenuAudience;
  title: string;
  description: string;
  features: ClientFeaturesShape;
  clientName: string;
  disabled?: boolean;
  onMasterChange: (checked: boolean) => void;
  onOverrideChange: (key: MenuKey, next: OverrideState) => void;
  idPrefix: string;
}) {
  const [open, setOpen] = useState(audience === 'broker');
  const flags = MENU_FLAGS.filter(
    (f) => f.audience === audience && isMenuFlagVisibleForClient(f, clientName),
  );

  const visibleCount = flags.filter((f) => resolveMenuItemEnabled(f.key, features)).length;
  const overrideCount = flags.filter((f) => overrideStateFor(features, f.key) !== 'default').length;
  const masterId = `${idPrefix}-master-${audience}`;

  return (
    <div className='border border-gray-200 rounded-lg overflow-hidden bg-white'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left'
      >
        <div className='flex items-center gap-2'>
          {open ? (
            <ChevronDown className='h-4 w-4 text-brand-gray' />
          ) : (
            <ChevronRight className='h-4 w-4 text-brand-gray' />
          )}
          <div>
            <div className='text-sm font-semibold text-brand-navy'>{title}</div>
            <div className='text-xs text-brand-gray'>{description}</div>
          </div>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <Badge variant='outline' className='text-xs'>
            {visibleCount} / {flags.length} visible
          </Badge>
          {overrideCount > 0 && (
            <Badge className='bg-amber/10 text-amber border-amber/20 text-xs'>
              {overrideCount} override{overrideCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </button>

      {open && (
        <div className='p-4 space-y-4'>
          {audience === 'broker' && (
            <div className='flex items-start gap-3 pb-4 border-b border-gray-100'>
              <Switch
                id={masterId}
                checked={!!features.enhancedBrokerPortal}
                disabled={disabled}
                onCheckedChange={onMasterChange}
                className='mt-1'
              />
              <div className='space-y-1'>
                <Label htmlFor={masterId} className='font-semibold cursor-pointer text-brand-navy'>
                  Enhanced Broker Portal (master)
                </Label>
                <p className='text-sm text-brand-gray'>
                  Default visibility for every broker-portal item below. Items with an explicit
                  override ignore this toggle.
                </p>
              </div>
            </div>
          )}

          <div className='space-y-3'>
            {flags.map((flag) => (
              <FlagOverrideRow
                key={flag.key}
                flag={flag}
                state={overrideStateFor(features, flag.key)}
                effective={resolveMenuItemEnabled(flag.key, features)}
                masterOn={!!features.enhancedBrokerPortal}
                disabled={disabled}
                onChange={(next) => onOverrideChange(flag.key, next)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FlagOverrideRow({
  flag,
  state,
  effective,
  masterOn,
  disabled,
  onChange,
}: {
  flag: MenuFlagDefinition;
  state: OverrideState;
  effective: boolean;
  masterOn: boolean;
  disabled?: boolean;
  onChange: (next: OverrideState) => void;
}) {
  return (
    <div className='flex items-center justify-between gap-4 py-2'>
      <div className='min-w-0'>
        <div className='flex items-center gap-2'>
          <div className='text-sm font-medium text-brand-navy truncate'>{flag.label}</div>
          {flag.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className='h-3.5 w-3.5 text-brand-gray shrink-0' />
                </TooltipTrigger>
                <TooltipContent className='max-w-xs'>{flag.description}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className='text-xs text-brand-gray flex items-center gap-2'>
          <span>{defaultLabel(flag, masterOn)}</span>
          <span className='text-gray-300'>·</span>
          <span className={effective ? 'text-emerald' : 'text-red-500'}>
            Effective: {effective ? 'visible' : 'hidden'}
          </span>
        </div>
      </div>

      <div className='flex items-center gap-1 shrink-0'>
        <OverrideButton
          label='Default'
          icon={<MinusCircle className='h-3.5 w-3.5' />}
          active={state === 'default'}
          disabled={disabled}
          onClick={() => onChange('default')}
        />
        <OverrideButton
          label='On'
          icon={<CheckCircle className='h-3.5 w-3.5' />}
          active={state === 'on'}
          disabled={disabled}
          onClick={() => onChange('on')}
        />
        <OverrideButton
          label='Off'
          icon={<XCircle className='h-3.5 w-3.5' />}
          active={state === 'off'}
          disabled={disabled}
          onClick={() => onChange('off')}
        />
      </div>
    </div>
  );
}

function OverrideButton({
  label,
  icon,
  active,
  disabled,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type='button'
      variant={active ? 'default' : 'outline'}
      size='sm'
      disabled={disabled}
      onClick={onClick}
      className='h-7 px-2 gap-1 text-xs'
    >
      {icon}
      {label}
    </Button>
  );
}
